import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCart } from "../../reducers/rifa/rifaActions";
import { hasLogin, hasPixParam, hasWalletParam, hasWrapper } from "../../utils/getPreTickets";
import { R$ } from "../utils/brl";
import NumberGridBicho from "./numberGridBicho";
import { emitIntent } from "../../newDesign/utils/emitIntent";
import { saveCart, getSavedCart, removeSavedCart } from "../../modules/localStorage";
import numberBichoData from "../../utils/numberBichoData";
import { Cart as FloatingCart } from "./layout/cart/Cart";
import { RifaBuyerRanking } from "./layout/buyerRanking";

import {
  AnalyticsSetGameStep
} from "../../reducers/analytics/analyticsActions";

const MOBILE_BREAKPOINT = 900;
const SECTION_LENGTH = 500;

function SelectBicho({ rifa, disabledNumbers, finishBet, calculateAvailableQuotas, minimumAvailableQuotas }) {
  const cart = useSelector((state) => state.rifa.cart);
  
  const dispatch = useDispatch();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [loading, setLoading] = useState(false);

  const isMobile = windowWidth < MOBILE_BREAKPOINT;
  const isDesktop = !isMobile;

  const numberAmount = rifa.numberAmount;
  const numberArray = new Array(numberAmount).fill().map((_, index) => index + 1);

  useEffect(() => {
    function handleWindowSizeChange() {
      setWindowWidth(window.innerWidth);
    }

    const savedCart = getSavedCart(rifa, 'rifa-cart');
    if(savedCart){
      dispatch(setCart(savedCart));
    }

    window.addEventListener("resize", handleWindowSizeChange);

    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const enabledNumberArray = useMemo(() => {
    const numberLength = Math.log10(rifa.numberAmount);

    return numberArray.filter(
      (num) => !disabledNumbers[num.toString().padStart(numberLength, 0)]
    );
  }, [numberArray, disabledNumbers]);

  const selections = useMemo(() => {
    return cart.map((bet) => bet.number);
  }, [cart]);

  const numberLength = Math.floor(Math.log10(numberAmount - 1)) + 1;

  const allowMultiSelection = useMemo(() => {
    return rifa && ["sem-validacao"].includes(rifa.tipo);
  }, [rifa.tipo]);

  /**
   * @param {number} number
   * @returns {string|null}
   */
  function selectNumber(number) {
    dispatch(AnalyticsSetGameStep("Select Number"));
    if (typeof number != "number") number = parseInt(number);

    if (isNaN(number)) return "Digite um número";
    if (number < 0 || number > numberAmount) return "Número não encontrado";

    const numberLength = Math.log10(rifa.numberAmount);
    if (disabledNumbers[number.toString().padStart(numberLength, 0)]) {
      return "Número já reservado.";
    }

    if (rifa.tipo === "com-validacao" && selections.includes(number)) {
      return "Número já está no carrinho.";
    }

    if (isNumberSelected(number)) {
      return incrementNumber(number);
    }

    const newcart = [...cart];

    newcart.push({
      number,
      value: rifa.value || 0,
      amount: 1,
    });

    dispatch(setCart(newcart));
    saveCart(rifa, newcart, 'rifa-cart');

    return "";
  }

  /**
   * @param {number} number
   */
  function deselectNumber(number) {
    const newcart = cart.filter((bet) => bet.number != number);

    dispatch(setCart(newcart));
    saveCart(rifa, newcart, 'rifa-cart');
  }

  function applySurpresinha(length = 1) {
    dispatch(AnalyticsSetGameStep("Chose Lucky Number"));
    for (let i = 0; i < length; i++) {
      let isNumberAvailable = !(rifa.tipo === "com-validacao");
      do {
        const number = Math.floor(Math.random() * enabledNumberArray.length);
        if (!selections.includes(enabledNumberArray[number])) {
          isNumberAvailable = true;
          selectNumber(enabledNumberArray[number]);
        }
      } while (isNumberAvailable === false);
    }
  }

  function isNumberSelected(number) {
    return !!cart.find((bet) => bet.number == number);
  }

  /**
   * @param {number} number
   */
  function incrementNumber(number) {
    const newcart = [...cart];

    const bet = newcart.find((bet) => bet.number === number);
    if (!bet) return;

    bet.amount++;

    dispatch(setCart(newcart));
    saveCart(rifa, newcart, 'rifa-cart');
  }

  /**
   * @param {number} index
   */
  function decrementNumber(number) {
    const newcart = [...cart];

    const bet = newcart.find((bet) => bet.number === number);
    if (!bet) return;

    bet.amount--;

    dispatch(setCart(newcart));
    saveCart(rifa, newcart, 'rifa-cart');

    if (bet.amount <= 0) deselectNumber(number);
  }

  /**
   * @param {boolean} bypassConfirmation
   */
  function clearCart(bypassConfirmation = false) {
    dispatch(AnalyticsSetGameStep("Clear Cart"));
    if (
      bypassConfirmation ||
      window.confirm("Tem certeza de quer limpar o carrinho?")
    ) {
      dispatch(setCart([]));
      removeSavedCart('rifa-cart');
    }
    setLoading(false);
  }

  function sendBet(cart, clearCart, isPreTicket, isPix) {
    setLoading(true);
    saveCart(rifa, cart, 'rifa-cart');
    finishBet(cart, clearCart, isPreTicket, isPix)
  }

  if (!enabledNumberArray.length) {
    return (
      <p className="rifa-title text-center">
        Todos os números já foram reservados
      </p>
    );
  }

  const validateWithLogin = () => {
    if (hasLogin()) {
      removeSavedCart('rifa-cart');
      sendBet({ cart }, clearCart, false, true)
    } else {
      saveCart(rifa, cart, 'rifa-cart');
      emitIntent("open_login");
    }
  }

  const minimumQuotas = Math.max(1, minimumAvailableQuotas || 1);

  function bichoData(number) {
    const bicho = numberBichoData.filter(bicho=>bicho.number===number.toString().padStart(2, "0"))[0]
    const data = {
      ...bicho,
      groupNumbers: new Array(4).fill().map((_, i) => ((4 * number + i - 3) % 100).toString().padStart(2, '0')).join(', ')
    }
    return data;
  }
  
  const useOldCart = !hasWrapper();

  function renderCart() {
    return (
      <React.Fragment>
        { !useOldCart 
          ? (<FloatingCart
          totalAmount={R$(cart.reduce((total, rifa) => total + (rifa.value * rifa.amount), 0))}
          bets={cart.map((bet) => ({number: bet.number, label: String(bet.number), amount: rifa.tipo === "sem-validacao" ? bet.amount : 1, alternativeText: bichoData(bet.number).name}))}
          maxNumberSize={rifa.game.tnyTamanhoMax}
          onFinishClick={() => validateWithLogin()}
          onRemoveClick={(number) => rifa.tipo === "sem-validacao" ? decrementNumber(number) : deselectNumber(number)}
          onClearClick={clearCart}
          keepOpenWhenEmpty={false}
        ></FloatingCart>)
        : cart.reduce((total, rifa) => total + rifa.value, 0) > 0 &&
          <React.Fragment>
            <p className="rifa-due__result">Valor total: {R$(cart.reduce((total, rifa) => total + rifa.value * rifa.amount, 0))}</p>
            <div className="rifa-cart-button-container">
              {(cart.reduce((total, rifa) => total + rifa.value, 0) > 0 && selections.length >= minimumQuotas && hasWalletParam()) &&
                <button
                  className="button submit-button rifa-due__submit-button"
                  onClick={() => sendBet({ cart }, clearCart, false, false)}
                >
                  Finalizar compra
                </button>
              }
              {(hasPixParam() && cart.reduce((total, rifa) => total + rifa.value, 0) > 0 && selections.length >= minimumQuotas) &&
                <button
                  className="button submit-button rifa-due__submit-button"
                  onClick={() => validateWithLogin()}
                >
                  Pagar Agora!
                </button>
              }
            </div> 
         </React.Fragment>}


      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <p className="rifa-title" style={{ margin: '0px' }}>Cotas Disponíveis</p>
      <div className="quota-container">
        {calculateAvailableQuotas &&
          <h4><span>{`${calculateAvailableQuotas}%`}</span>{` de Cotas Disponíveis.`}</h4>
        }
      </div>
      <section className="column-rifa">
        <main className="game bicho">
          <NumberGridBicho
            array={numberArray}
            selections={selections}
            sectionLength={SECTION_LENGTH}
            onSelect={selectNumber}
            numberLength={numberLength}
            allowMultiSelection={allowMultiSelection}
            onDeselectNumber={deselectNumber}
            disabledNumbers={disabledNumbers}
          />
        </main>
        {isDesktop ? (
          <aside class="cart-wrapper">
            {renderCart()}
          </aside>
        ) : null}
      </section>
      {isMobile ? (
        <React.Fragment>
          {renderCart()}
        </React.Fragment>
      ) : null}

    </React.Fragment>
  );
}

export default SelectBicho;



// WEBPACK FOOTER //
// ./src/rifa/components/selectBicho.jsx